import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '../icons/CloseIcon.jsx';
import { requestOTCtoChangeCertificate, sendChangeRequest, validateOTCtoChangeCertificate } from '../../interact.jsx';

export default function CertificateChangeDataDialog({ open, onClose, onSendEvent, certificate }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [section, setSection] = useState('step1');
    const [otc, setOtc] = useState(Array(6).fill(''));
    const [isOtpInvalid, setIsOtpInvalid] = useState(false);
    const [token, setToken] = useState('');
    const [currentName, setCurrentName] = useState('');
    const [updatedCertificateValues, setUpdatedCertificateValues] = useState({
        first_name: '',
        last_name: '',
    });

    const udpateValueFieldChange = (field, value) => {
        setUpdatedCertificateValues((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const isUpdatedFieldsFormValid = () => {
        return (
            updatedCertificateValues['first_name'] &&
            updatedCertificateValues['first_name'].trim() &&
            updatedCertificateValues['last_name'] &&
            updatedCertificateValues['last_name'].trim()
        );
    };

    const isOtcComplete = otc.every((digit) => digit !== '');
    const isEmailValid = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };
    const otcChange = (value, index) => {
        const newOtc = [...otc];
        newOtc[index] = value;
        setOtc(newOtc);
        setIsOtpInvalid(false);
    };

    const handleConfirmStep1 = () => {
        //Request OTC
        requestOTCtoChangeCertificate(certificate.id, email).then((result) => {
            setSection('step2');
        });
    };

    const resendOtc = () => {
        requestOTCtoChangeCertificate(certificate.id, email).then((result) => {
            // Maybe show an OK message?
        });
    };

    const handleConfirmStep2 = (pass) => {
        //Validate
        setIsOtpInvalid(false);
        validateOTCtoChangeCertificate(certificate.id, pass.join(''))
            .then((result) => {
                setToken(result.token);
                setCurrentName(result.name || '');
                setSection('step3');
            })
            .catch((error) => {
                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            setIsOtpInvalid(true);
                            break;
                        case 409:
                            window.location.reload();
                            break;
                        default: {
                            setIsOtpInvalid(true);
                            console.error('Unexpected error:', error);
                            break;
                        }
                    }
                } else {
                    console.error('Unexpected error:', error);
                }
            });
    };

    const handleConfirmStep4 = () => {
        sendChangeRequest(certificate.id, token, updatedCertificateValues)
            .then((result) => {
                setSection('step5');
            })
            .catch((error) => {
                console.error('Unexpected error:', error);
                setSection('step1');
            });
    };
    const handlePaste = (event) => {
        const pasteData = event.clipboardData.getData('text').slice(0, 6);
        if (/^\d{1,6}$/.test(pasteData)) {
            const newValues = pasteData.split('');
            setOtc(newValues);
            handleConfirmStep2(newValues);
        }
        event.preventDefault();
    };

    return (
        <>
            {section !== 'step4' && section !== 'step5' && (
                <Dialog
                    open={open}
                    onClose={onClose}
                    sx={{
                        '& .MuiPaper-root': {
                            padding: 1,
                            borderRadius: '16px',
                        },
                    }}
                >
                    <DialogTitle>
                        <Typography component="span" variant="h3">
                            {t('certificate.change_data.form_title')}
                        </Typography>
                        <a onClick={onClose} style={{ position: 'absolute', right: 12, top: 12, cursor: 'pointer' }}>
                            <CloseIcon />
                        </a>
                    </DialogTitle>
                    <DialogContent>
                        {section === 'step1' && (
                            <Box>
                                <Typography variant="body2">
                                    {t('certificate.change_data.step1_description')}
                                </Typography>
                                <Typography variant="h6" fontSize={18} fontWeight={700} sx={{ marginTop: 3 }}>
                                    {t('certificate.change_data.step1_validate_email')}
                                </Typography>
                                <TextField
                                    fullWidth
                                    label={t('certificate.change_data.step1_email_placeholder')}
                                    variant="outlined"
                                    value={email}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && isEmailValid(email)) handleConfirmStep1();
                                    }}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoFocus
                                    slotProps={{
                                        input: {
                                            sx: {
                                                borderRadius: '12px',
                                            },
                                        },
                                        inputLabel: {
                                            sx: {
                                                color: 'grey',
                                            },
                                        },
                                    }}
                                    sx={{ marginTop: 3 }}
                                />
                                <Box textAlign="center" sx={{ marginTop: 4 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleConfirmStep1}
                                        disabled={!isEmailValid(email)}
                                        sx={{
                                            padding: '12px 24px',
                                            fontSize: '16px',
                                            borderRadius: '12px',
                                        }}
                                    >
                                        {t('certificate.change_data.step1_button_confirm')}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {section === 'step2' && (
                            <Box>
                                <Typography variant="body2">
                                    {t('certificate.change_data.step2_description')}
                                </Typography>
                                <Typography variant="h6" fontSize={18} fontWeight={700} sx={{ marginTop: 2 }}>
                                    {t('certificate.change_data.step2_enter_code')}
                                </Typography>
                                <Box display="flex" justifyContent="center" sx={{ marginTop: 2 }} onPaste={handlePaste}>
                                    {otc.map((digit, index) => (
                                        <TextField
                                            key={index}
                                            value={digit}
                                            onChange={(e) => otcChange(e.target.value, index)}
                                            slotProps={{
                                                input: { maxLength: 1, style: { textAlign: 'center' } },
                                            }}
                                            id={`otc-input-${index}`}
                                            autoFocus={index === 0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Backspace' && !otc[index]) {
                                                    if (index > 0) {
                                                        const prevInput = document.getElementById(
                                                            `otc-input-${index - 1}`,
                                                        );
                                                        if (prevInput) prevInput.focus();
                                                    }
                                                } else if (e.key === 'Enter' && !isOtpInvalid) {
                                                    handleConfirmStep2(otc);
                                                }
                                            }}
                                            onInput={(e) => {
                                                if (/^\d$/.test(e.target.value)) {
                                                    if (index < otc.length - 1) {
                                                        const nextInput = document.getElementById(
                                                            `otc-input-${index + 1}`,
                                                        );
                                                        if (nextInput) nextInput.focus();
                                                    }
                                                } else {
                                                    e.target.value = '';
                                                }
                                            }}
                                            sx={{
                                                width: '50px',
                                                margin: '0 8px',
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: isOtpInvalid ? 'red' : '#000',
                                                    },
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                                {isOtpInvalid && (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        sx={{ marginTop: 2, textAlign: 'center' }}
                                    >
                                        {t('certificate.change_data.step2_error_message')}
                                    </Typography>
                                )}
                                <Box sx={{ marginTop: 2 }}>
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={resendOtc}
                                        sx={{
                                            fontSize: '12px',
                                            textDecoration: 'none',
                                            color: '#548CFF',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {t('certificate.change_data.step2_action_resend')}
                                    </Link>
                                </Box>
                                <Box textAlign="center" sx={{ marginTop: 4 }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleConfirmStep2(otc)}
                                        disabled={!isOtcComplete}
                                        sx={{
                                            padding: '12px 24px',
                                            fontSize: '16px',
                                            borderRadius: '12px',
                                        }}
                                    >
                                        {t('certificate.change_data.step2_button_validate')}
                                    </Button>
                                </Box>
                                <Box textAlign="center" sx={{ marginTop: 2 }}>
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() => {
                                            setSection('step1');
                                        }}
                                        sx={{
                                            fontSize: '12px',
                                            textDecoration: 'none',
                                            fontWeight: '700',
                                            color: '#548CFF',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {t('certificate.change_data.step2_action_restart')}
                                    </Link>
                                </Box>
                            </Box>
                        )}
                        {section === 'step3' && (
                            <>
                                <Box>
                                    <Typography variant="body2">
                                        {t('certificate.change_data.step3_descripcion')}
                                    </Typography>
                                </Box>
                                <Grid container spacing={2} mb={2} key={'name'} alignItems="center">
                                    <Grid size={12} sx={{ marginTop: 2 }}>
                                        <Typography variant="h5" sx={{ fontSize: '18px', fontWeight: 700 }}>
                                            {t(`certificate.change_data.step3_option_name_current`)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ marginTop: 2 }}>
                                            {currentName}
                                        </Typography>
                                    </Grid>
                                    <Grid size={6} sx={{ marginTop: 3 }}>
                                        <Typography variant="h5" sx={{ fontSize: '18px', fontWeight: 700 }}>
                                            {t(`certificate.change_data.step3_option_first_name_correct`)}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            label={t(`certificate.change_data.step3_option_first_name_correct`)}
                                            autoFocus
                                            onChange={(e) => udpateValueFieldChange('first_name', e.target.value)}
                                            sx={{
                                                marginBottom: 1,
                                                marginTop: 2.3,
                                            }}
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                    },
                                                },
                                                inputLabel: {
                                                    sx: {
                                                        color: 'grey',
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6} sx={{ marginTop: 3 }}>
                                        <Typography variant="h5" sx={{ fontSize: '18px', fontWeight: 700 }}>
                                            {t(`certificate.change_data.step3_option_last_name_correct`)}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            label={t(`certificate.change_data.step3_option_last_name_correct`)}
                                            onChange={(e) => udpateValueFieldChange('last_name', e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && isUpdatedFieldsFormValid())
                                                    setSection('step4');
                                            }}
                                            sx={{
                                                marginBottom: 1,
                                                marginTop: 2.3,
                                            }}
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                    },
                                                },
                                                inputLabel: {
                                                    sx: {
                                                        color: 'grey',
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Box display="flex" justifyContent="flex-end" mt={4}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setSection('step4')}
                                        disabled={!isUpdatedFieldsFormValid()}
                                        sx={{
                                            padding: '12px 24px',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        {t('certificate.change_data.step3_button_send')}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            )}
            {section === 'step4' && (
                <Dialog
                    open={open}
                    onClose={onClose}
                    sx={{
                        '& .MuiPaper-root': {
                            padding: 2,
                            borderRadius: '16px',
                            width: '540px',
                        },
                    }}
                >
                    <DialogTitle>
                        <Typography component="span" variant="h3">
                            {t('certificate.change_data.confirm_title')}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box>
                            <Typography variant="body2">{t('certificate.change_data.confirm_description')}</Typography>
                        </Box>
                        <Box textAlign="center" sx={{ marginTop: 4 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    borderRadius: '12px',
                                    marginRight: 2,
                                }}
                            >
                                {t('certificate.change_data.step4_button_cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirmStep4}
                                sx={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    borderRadius: '12px',
                                }}
                            >
                                {t('certificate.change_data.step4_button_confirm')}
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
            {section === 'step5' && (
                <Dialog
                    open={open}
                    onClose={onClose}
                    sx={{
                        '& .MuiPaper-root': {
                            padding: 1,
                            borderRadius: '16px',
                            width: '540px',
                        },
                    }}
                >
                    <DialogTitle>
                        <Typography component="span" variant="h3">
                            {t('certificate.change_data.result_title')}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box>
                            <Typography variant="body2">{t('certificate.change_data.result_description')}</Typography>
                            <a
                                onClick={onClose}
                                style={{ position: 'absolute', right: 12, top: 12, cursor: 'pointer' }}
                            >
                                <CloseIcon />
                            </a>
                        </Box>
                        <Box textAlign="center" sx={{ marginTop: 4 }}>
                            <Button
                                variant="contained"
                                onClick={onClose}
                                sx={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    borderRadius: '12px',
                                }}
                            >
                                {t('certificate.change_data.step5_button_confirm')}
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
